import { CloseOutlined } from '@ant-design/icons';
import { Select, Table } from 'antd';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import s from './styles.module.scss';

import type { IMemberFormValue } from '../../validation-schema';
import type { ColumnsType } from 'antd/es/table';
import type { FC } from 'react';

interface IMemberAssignmentTableProps {
  members: IMemberFormValue[];
  onRemove: (loginGuid: string) => void;
  onPriorityChange: (
    loginGuid: string,
    priority: 'primary' | 'secondary' | 'tertiary',
  ) => void;
}

const PRIORITY_OPTIONS = [
  { label: 'Primary', value: 'primary' },
  { label: 'Secondary', value: 'secondary' },
  { label: 'Tertiary', value: 'tertiary' },
];

const MemberAssignmentTable: FC<IMemberAssignmentTableProps> = ({
  members,
  onRemove,
  onPriorityChange,
}) => {
  const { t } = useTranslation();
  const tableRef = useRef<HTMLDivElement>(null);
  const prevCountRef = useRef(0);

  useEffect(() => {
    if (members.length > prevCountRef.current && tableRef.current) {
      const scrollContainer = tableRef.current.closest('[class*="body"]');
      scrollContainer?.scrollTo({
        top: scrollContainer.scrollHeight,
        behavior: 'smooth',
      });
    }
    prevCountRef.current = members.length;
  }, [members.length]);

  const columns: ColumnsType<IMemberFormValue> = [
    {
      title: t('labels.name'),
      dataIndex: 'name',
      key: 'name',
      width: '35%',
      ellipsis: { showTitle: true },
    },
    {
      title: t('labels.role'),
      dataIndex: 'role',
      key: 'role',
      width: '15%',
    },
    {
      title: t('labels.podPriority'),
      dataIndex: 'priority',
      key: 'priority',
      width: 220,
      render: (_: unknown, record: IMemberFormValue) => (
        <span data-cy={`member-priority__${record.loginGuid}`}>
          <Select
            value={record.priority}
            options={PRIORITY_OPTIONS.map((opt) => ({
              ...opt,
              label: t(`labels.priority${opt.label}`, ''),
            }))}
            onChange={(value: 'primary' | 'secondary' | 'tertiary') =>
              onPriorityChange(record.loginGuid, value)
            }
            className={s.prioritySelect}
          />
        </span>
      ),
    },
    {
      title: '',
      key: 'remove',
      width: 40,
      render: (_: unknown, record: IMemberFormValue) => (
        <button
          type="button"
          className={s.removeButton}
          onClick={() => onRemove(record.loginGuid)}
          aria-label={t('labels.removeMemberAriaLabel', {
            memberName: record.name,
          })}
          data-cy={`member-remove__${record.loginGuid}`}
        >
          <CloseOutlined className={s.removeIcon} />
        </button>
      ),
    },
  ];

  return (
    <div
      ref={tableRef}
      data-cy="member-assignment-table"
      className={s.tableWrapper}
    >
      <Table
        columns={columns}
        dataSource={members}
        rowKey="loginGuid"
        pagination={false}
        className={s.table}
        locale={{
          emptyText: t('labels.noMembersAssigned'),
        }}
      />
    </div>
  );
};

export default MemberAssignmentTable;
